import { useMemo } from 'react';
import type { JSX } from 'react';
import type { Palette } from '../../../_theme/palette.js';
import { hexWithAlpha } from '../../../_theme/palette.js';

interface AnnouncementHtmlBodyProps {
  palette: Palette;
  html: string;
}

// Mirrors src/lib/TiptapEditor/styles/partials/*.css so SDK render matches the editor.
export function AnnouncementHtmlBody({ palette, html }: AnnouncementHtmlBodyProps): JSX.Element {
  const css = useMemo(() => buildScopedCss(palette), [palette]);
  return (
    <>
      <style>{css}</style>
      <div className="chirp-ann-body" dangerouslySetInnerHTML={{ __html: html }} />
    </>
  );
}

function buildScopedCss(palette: Palette): string {
  const text = palette.text;
  const muted = palette.textMuted;
  const primary = palette.primary;
  const borderSoft = hexWithAlpha(text, 0.08);
  const borderMid = hexWithAlpha(text, 0.1);
  const codeBg = hexWithAlpha(text, 0.05);
  const quoteBg = hexWithAlpha(primary, 0.05);
  const quoteBorder = hexWithAlpha(primary, 0.35);

  return `
    .chirp-ann-body {
      color: ${text};
      font-size: 16px;
      line-height: 1.625;
      font-weight: 400;
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      text-rendering: optimizeLegibility;
    }
    .chirp-ann-body > :first-child { margin-top: 0 !important; }
    .chirp-ann-body > :last-child { margin-bottom: 0 !important; }

    /* Paragraphs: leading-relaxed + my-3 (inside blocks) / my-6 (top-level) */
    .chirp-ann-body p { margin: 12px 0; line-height: 1.625; }
    .chirp-ann-body > p { margin: 24px 0; }

    /* Headings: font-bold, Tailwind sizes, mt-12 / mt-8 */
    .chirp-ann-body h1, .chirp-ann-body h2, .chirp-ann-body h3,
    .chirp-ann-body h4, .chirp-ann-body h5, .chirp-ann-body h6 {
      font-weight: 700;
      line-height: 1.25;
      color: ${text};
    }
    .chirp-ann-body h1 { font-size: 30px; }
    .chirp-ann-body h2 { font-size: 24px; }
    .chirp-ann-body h3 { font-size: 20px; }
    .chirp-ann-body h4 { font-size: 18px; }
    .chirp-ann-body h5 { font-size: 16px; }
    .chirp-ann-body h6 { font-size: 14px; }
    .chirp-ann-body h1, .chirp-ann-body h2, .chirp-ann-body h3 { margin: 48px 0 0; }
    .chirp-ann-body h4, .chirp-ann-body h5, .chirp-ann-body h6 { margin: 32px 0 0; }

    /* Inline */
    .chirp-ann-body a { color: ${primary}; text-decoration: underline; text-underline-offset: 2px; }
    .chirp-ann-body strong, .chirp-ann-body b { font-weight: 700; }
    .chirp-ann-body em, .chirp-ann-body i { font-style: italic; }
    .chirp-ann-body u { text-decoration: underline; }
    .chirp-ann-body s { text-decoration: line-through; }
    .chirp-ann-body mark { background: ${hexWithAlpha(primary, 0.14)}; padding: 0 3px; border-radius: 3px; color: inherit; }

    /* Lists */
    .chirp-ann-body ul, .chirp-ann-body ol { margin: 16px 0; padding: 0 0 0 22px; line-height: 1.625; }
    .chirp-ann-body ul { list-style: disc; }
    .chirp-ann-body ol { list-style: decimal; }
    .chirp-ann-body li { margin: 4px 0; }
    .chirp-ann-body li > p { margin: 0; }
    .chirp-ann-body li ul, .chirp-ann-body li ol { margin: 4px 0; }
    .chirp-ann-body ul[data-type="taskList"] { list-style: none; padding-left: 2px; }
    .chirp-ann-body ul[data-type="taskList"] li { display: flex; align-items: flex-start; gap: 8px; }
    .chirp-ann-body ul[data-type="taskList"] li > label { flex-shrink: 0; margin-top: 3px; }
    .chirp-ann-body ul[data-type="taskList"] li > div { flex: 1; }
    .chirp-ann-body ul[data-type="taskList"] li[data-checked="true"] > div { color: ${muted}; text-decoration: line-through; }
    .chirp-ann-body input[type="checkbox"] { accent-color: ${primary}; cursor: default; }

    /* Blockquote figure (editor's custom block) */
    .chirp-ann-body figure[data-type="blockquoteFigure"] { margin: 24px 0; padding: 0; }
    .chirp-ann-body figure[data-type="blockquoteFigure"] blockquote {
      margin: 0;
      padding: 10px 16px;
      border-left: 2px solid ${quoteBorder};
      background: ${quoteBg};
      border-radius: 0 6px 6px 0;
    }
    .chirp-ann-body figure[data-type="blockquoteFigure"] blockquote p { margin: 0; line-height: 1.625; }
    .chirp-ann-body figure[data-type="blockquoteFigure"] figcaption {
      margin-top: 8px;
      padding-left: 18px;
      font-size: 13px;
      color: ${muted};
      font-style: normal;
      text-align: left;
    }
    .chirp-ann-body blockquote {
      margin: 16px 0;
      padding: 10px 16px;
      border-left: 2px solid ${quoteBorder};
      background: ${quoteBg};
      border-radius: 0 6px 6px 0;
    }
    .chirp-ann-body blockquote p { margin: 0; }
    .chirp-ann-body figure { margin: 24px 0; }
    .chirp-ann-body figcaption { font-size: 12px; color: ${muted}; text-align: center; margin-top: 8px; }

    /* Code */
    .chirp-ann-body code {
      background: ${codeBg};
      padding: 2px 6px;
      border-radius: 4px;
      font-size: 0.88em;
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, monospace;
      color: ${text};
    }
    .chirp-ann-body pre {
      margin: 24px 0;
      padding: 14px 16px;
      background: #0f172a;
      color: #e2e8f0;
      border-radius: ${palette.radius}px;
      font-size: 13px;
      line-height: 1.55;
      overflow-x: auto;
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, monospace;
    }
    .chirp-ann-body pre code { background: transparent; padding: 0; color: inherit; font-size: inherit; }

    /* Divider / media */
    .chirp-ann-body hr { border: none; border-top: 1px solid ${borderSoft}; margin: 32px 0; }
    .chirp-ann-body img { max-width: 100%; width: 100%; height: auto; display: block; margin: 24px 0; border-radius: ${palette.radius}px; }

    /* Tables — thinner borders, airy */
    .chirp-ann-body table {
      width: 100%;
      border-collapse: collapse;
      margin: 24px 0;
      font-size: 14px;
      border-top: 1px solid ${borderSoft};
      border-left: 1px solid ${borderSoft};
    }
    .chirp-ann-body th, .chirp-ann-body td {
      border-right: 1px solid ${borderSoft};
      border-bottom: 1px solid ${borderSoft};
      padding: 8px 12px;
      text-align: left;
      vertical-align: top;
      font-weight: 400;
    }
    .chirp-ann-body th { background: ${hexWithAlpha(text, 0.03)}; font-weight: 600; color: ${text}; }
    .chirp-ann-body th p, .chirp-ann-body td p { margin: 0; line-height: 1.5; }

    /* Details */
    .chirp-ann-body details { margin: 16px 0; padding: 10px 14px; border: 1px solid ${borderMid}; border-radius: 8px; }
    .chirp-ann-body summary { cursor: pointer; font-weight: 600; }
  `;
}
